import { Button, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import React, { useMemo } from 'react';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';

import { MxAccount, MxTransaction } from '../../models/mx';
import {
  DatePickerField,
  FullWidthTextField,
  SelectField,
} from '../shared/Forms/FullWidthForm/fullWidthFormComponents';
import { decimal, nonNegative, required } from '../shared/Forms/validations';

export type EditTransactionFormValues = Exclude<MxTransaction, 'date'> & { date: string | Date };
type EditTransactionFormProps = {
  accounts: MxAccount[];
  onClose: () => void;
};

// prettier-ignore
const categories = [
  'Auto & Transport', 'Bills & Utilities', 'Business Services', 'Education', 'Entertainment', 'Fees & Charges', 'Financial', 'Food & Dining', 'Gifts & Donations', 'Health & Fitness', 'Home', 'Income', 'Investments', 'Kids', 'Personal Care', 'Pets', 'Shopping', 'Taxes', 'Transfer', 'Travel', 'Uncategorized'];
const categoryMap = categories.map((category) => ({ name: category, value: category }));
const typeMap = [
  { name: 'Income', value: 'CREDIT' },
  { name: 'Expense', value: 'DEBIT' },
];

const EditTransactionForm: React.FunctionComponent<
  EditTransactionFormProps & InjectedFormProps<EditTransactionFormValues, EditTransactionFormProps>
> = ({ accounts, handleSubmit, initialValues, onClose, submitting }) => {
  const accountMap = useMemo(() => {
    const newAccounts = accounts.map((account) => ({ name: account.name, value: account.guid }));
    newAccounts.unshift({ name: 'No Account', value: 'no-account' });
    return newAccounts;
  }, [accounts]);
  const statusMap = useMemo(() => {
    switch (initialValues.status) {
      case 'MANUAL':
        return [
          { name: 'Manual', value: 'MANUAL' },
          { name: 'Delete', value: 'DELETE' },
        ];
      case 'POSTED':
        return [
          { name: 'Posted', value: 'POSTED' },
          { name: 'Ignored', value: 'IGNORED' },
        ];
      case 'PENDING':
        return [
          { name: 'Pending', value: 'PENDING' },
          { name: 'Ignored', value: 'IGNORED' },
        ];
      case 'IGNORED':
        return [
          { name: 'Enable', value: 'ENABLE' },
          { name: 'Ignored', value: 'IGNORED' },
        ];
      default:
        return [{ name: 'Enable', value: 'ENABLE' }];
    }
  }, [initialValues.status]);

  const isManual = initialValues.status === 'MANUAL';

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>Edit a Transaction</DialogTitle>
      <DialogContent>
        <Field
          component={FullWidthTextField}
          label="Description"
          name="description"
          type="text"
          validate={required}
        />
        <Field
          component={FullWidthTextField}
          disabled={!isManual}
          fieldFormat="dollar"
          label="Amount"
          name="amount"
          parse={(value) => (value ? Number(value) : value)}
          type="number"
          validate={[decimal, nonNegative, required]}
        />
        <Field
          component={DatePickerField}
          disabled={!isManual}
          label="Date"
          name="date"
          type="date"
          validate={required}
        />
        <Field
          component={SelectField}
          disabled={!isManual}
          dataMap={accountMap}
          label="Account"
          name="account_guid"
          validate={required}
        />
        <Field
          component={SelectField}
          dataMap={categoryMap}
          label="Category"
          name="top_level_category"
          validate={required}
        />
        <Field
          component={SelectField}
          dataMap={typeMap}
          disabled={!isManual}
          label="Type"
          name="type"
          validate={required}
        />
        <Field
          component={SelectField}
          dataMap={statusMap}
          label="Status"
          name="status"
          validate={required}
        />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose} variant="contained">
          Cancel
        </Button>
        <Button color="primary" disabled={submitting} type="submit" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </form>
  );
};

export default reduxForm<EditTransactionFormValues, EditTransactionFormProps>({
  form: 'editTransactionForm',
  enableReinitialize: true,
})(EditTransactionForm);
